import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Save, Star, Trash2, Camera, Settings, ExternalLink,
  ImagePlus, Search, X, Pin, Tag, Plus, Pencil, Archive, Building2, Upload,
  Folder, FolderOpen,
} from "lucide-react";
import {
  adminListPhotos, adminGetSettings, adminUpdateSettings, adminSyncDrive,
  adminUpdatePhoto, adminDeletePhoto, adminCreatePhoto, adminUploadPhotos,
  adminCreateProductTag, adminUpdateProductTag, adminDeleteProductTag,
  adminCreateIndustry, adminUpdateIndustry, adminDeleteIndustry,
  adminListProjects, adminCreateProject, adminUpdateProject, adminDeleteProject,
  adminRemovePhotoFromProject, adminSetProjectCover,
} from "../../../lib/portfolio";

const PortfolioTab = () => {
  const [photos, setPhotos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [taggingMode, setTaggingMode] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showTagManager, setShowTagManager] = useState(false);
  const [showIndustryManager, setShowIndustryManager] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [ph, s, pr] = await Promise.all([adminListPhotos(), adminGetSettings(), adminListProjects()]);
      setPhotos(ph);
      setSettings(s);
      setProjects(pr);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const handleSync = async () => {
    if (!settings?.drive_folder_url) {
      toast.error("Set the Drive folder URL in Settings first");
      setShowSettings(true);
      return;
    }
    if (!settings?.drive_api_key_configured) {
      toast.error("GOOGLE_DRIVE_API_KEY env var is not set on the server");
      return;
    }
    setBusy(true);
    try {
      const res = await adminSyncDrive();
      toast.success(`Synced — +${res.added} new · ${res.updated} updated · ${res.total_found} total`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return photos.filter((p) => {
      if (industryFilter && !(p.industries || []).includes(industryFilter)) return false;
      if (!q) return true;
      const hay = [
        p.filename, p.caption, ...(p.industries || []), ...(p.product_tags || []),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [photos, searchQ, industryFilter]);

  const handleDelete = async (p) => {
    const label = p.caption || p.filename || p.id;
    const note = p.source === "upload"
      ? "This permanently removes the file from your storage."
      : "The original stays in Google Drive.";
    if (!window.confirm(`Delete "${label}"?\n\n${note}`)) return;
    try {
      await adminDeletePhoto(p.id);
      toast.success("Deleted");
      await reload();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-portfolio-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <Camera size={20} /> Portfolio
          </h2>
          <p className="text-xs text-[color:var(--ups-subtext)] mt-1">
            {photos.length} photo{photos.length === 1 ? "" : "s"}
            {settings?.last_sync_at && <> · synced {new Date(settings.last_sync_at).toLocaleString()}</>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowIndustryManager(true)}
            className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 hover:bg-[color:var(--ups-muted)]"
            style={{ border: "1px solid var(--ups-border)" }}
            data-testid="portfolio-industry-manager-btn"
          >
            <Building2 size={14} /> Industries
          </button>
          <button
            onClick={() => setShowTagManager(true)}
            className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 hover:bg-[color:var(--ups-muted)]"
            style={{ border: "1px solid var(--ups-border)" }}
            data-testid="portfolio-tag-manager-btn"
          >
            <Tag size={14} /> Product Tags
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 hover:bg-[color:var(--ups-muted)]"
            style={{ border: "1px solid var(--ups-border)" }}
            data-testid="portfolio-settings-btn"
          >
            <Settings size={14} /> Settings
          </button>
          <button
            onClick={() => setShowManualAdd(true)}
            className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 hover:bg-[color:var(--ups-muted)]"
            style={{ border: "1px solid var(--ups-border)" }}
            data-testid="portfolio-manual-add-btn"
          >
            <ImagePlus size={14} /> Add by URL
          </button>
          <button
            onClick={() => setShowUpload(true)}
            className="btn-brutal px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5"
            data-testid="portfolio-upload-btn"
          >
            <Upload size={14} /> Upload Photos
          </button>
          <button
            onClick={handleSync}
            disabled={busy}
            className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 disabled:opacity-50 hover:bg-[color:var(--ups-muted)]"
            style={{ border: "1px solid var(--ups-border)" }}
            data-testid="portfolio-sync-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {busy ? "Syncing…" : "Sync Drive"}
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div
        className="flex items-center gap-2 flex-wrap p-3 rounded-sm"
        style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}
      >
        <div className="relative flex-1 min-w-[200px]">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ups-subtext)]" />
          <input
            type="search"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search filename, caption, tags…"
            className="input-swiss w-full pl-7 pr-3 py-1.5 rounded-sm text-[12px]"
            data-testid="portfolio-search"
          />
        </div>
        <select
          value={industryFilter}
          onChange={(e) => setIndustryFilter(e.target.value)}
          className="input-swiss px-2 py-1.5 rounded-sm text-[12px]"
          data-testid="portfolio-filter-industry"
        >
          <option value="">All industries</option>
          {(settings?.industries || []).filter((i) => !i.archived).map((i) => (
            <option key={i.slug} value={i.slug}>{i.label}</option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-[11px] font-mono ml-auto">
          <input
            type="checkbox"
            checked={taggingMode}
            onChange={(e) => setTaggingMode(e.target.checked)}
            data-testid="portfolio-tagging-mode"
          />
          Tagging mode (expand all)
        </label>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin" /></div>
      ) : (
        <>
          {/* Projects panel — single source of truth for grouped items */}
          {projects.length > 0 && (
            <div className="space-y-2" data-testid="portfolio-projects-panel">
              <div className="flex items-center gap-2">
                <FolderOpen size={14} />
                <h3 className="font-display text-[14px] font-bold uppercase tracking-[0.12em]">Projects ({projects.length})</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {projects.map((pr) => (
                  <ProjectCard
                    key={pr.id}
                    project={pr}
                    onEdit={() => setEditingProject(pr)}
                    onChanged={reload}
                  />
                ))}
              </div>
            </div>
          )}

          {filtered.length === 0 ? (
            <div
              className="p-10 text-center rounded-sm text-[color:var(--ups-subtext)]"
              style={{ background: "#fff", border: "1px dashed var(--ups-border)" }}
              data-testid="portfolio-empty"
            >
              {photos.length === 0
                ? <>No photos yet — hit <strong>Upload Photos</strong> to add your first.</>
                : <>No photos match the current filters.</>}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="portfolio-photo-grid">
              {filtered.map((p) => (
                <PhotoCard
                  key={p.id}
                  photo={p}
                  projectName={projects.find((pr) => pr.id === p.project_id)?.name}
                  industries={(settings?.industries || []).filter((i) => !i.archived)}
                  productTags={settings?.product_tags || []}
                  expanded={taggingMode}
                  onSaved={(updated) => setPhotos((prev) => prev.map((x) => x.id === p.id ? updated : x))}
                  onDelete={() => handleDelete(p)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {editingProject && (
        <ProjectEditModal
          project={editingProject}
          industries={(settings?.industries || []).filter((i) => !i.archived)}
          productTags={settings?.product_tags || []}
          onClose={() => setEditingProject(null)}
          onSaved={async () => { setEditingProject(null); await reload(); }}
        />
      )}

      {showSettings && (
        <PortfolioSettingsModal
          settings={settings}
          onClose={() => setShowSettings(false)}
          onSaved={(s) => { setSettings(s); setShowSettings(false); }}
        />
      )}

      {showManualAdd && (
        <ManualAddModal
          industries={(settings?.industries || []).filter((i) => !i.archived)}
          productTags={settings?.product_tags || []}
          onClose={() => setShowManualAdd(false)}
          onAdded={async () => { setShowManualAdd(false); await reload(); }}
        />
      )}

      {showUpload && (
        <UploadPhotosModal
          onClose={() => setShowUpload(false)}
          onUploaded={async () => { await reload(); }}
        />
      )}

      {showTagManager && (
        <ProductTagManagerModal
          tags={settings?.product_tags || []}
          categories={settings?.product_tag_categories || []}
          onClose={() => setShowTagManager(false)}
          onChange={async () => { await reload(); }}
        />
      )}

      {showIndustryManager && (
        <IndustryManagerModal
          industries={settings?.industries || []}
          onClose={() => setShowIndustryManager(false)}
          onChange={async () => { await reload(); }}
        />
      )}
    </div>
  );
};

/* ============================================================== */
/* Per-photo card with inline tag editor                          */
/* ============================================================== */
const PhotoCard = ({ photo, projectName, industries, productTags, expanded, onSaved, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    industries: photo.industries || [],
    product_tags: photo.product_tags || [],
    caption: photo.caption || "",
    featured: !!photo.featured,
  });
  const [saving, setSaving] = useState(false);
  const isOpen = expanded || open;

  const toggleIndustry = (slug) => {
    setDraft((d) => ({
      ...d,
      industries: d.industries.includes(slug)
        ? d.industries.filter((s) => s !== slug)
        : [...d.industries, slug],
    }));
  };
  const toggleTag = (tag) => {
    setDraft((d) => ({
      ...d,
      product_tags: d.product_tags.includes(tag)
        ? d.product_tags.filter((t) => t !== tag)
        : [...d.product_tags, tag],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await adminUpdatePhoto(photo.id, draft);
      onSaved(updated);
      toast.success("Saved");
      if (!expanded) setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleFeatured = async () => {
    try {
      const updated = await adminUpdatePhoto(photo.id, { featured: !photo.featured });
      onSaved(updated);
    } catch (e) {
      toast.error("Could not toggle featured");
    }
  };

  return (
    <div
      className="rounded-sm overflow-hidden bg-white border-2"
      style={{ borderColor: "var(--ups-border)" }}
      data-testid={`admin-photo-${photo.id}`}
    >
      <div className="relative aspect-video bg-stone-200 overflow-hidden">
        {photo.media_type === "video" ? (
          <video
            src={photo.image_url}
            preload="metadata"
            muted
            playsInline
            className="w-full h-full object-cover"
            data-testid={`photo-video-${photo.id}`}
          />
        ) : (
          <img
            src={photo.thumb_url || photo.image_url}
            alt={photo.caption || photo.filename}
            loading="lazy"
            className="w-full h-full object-cover"
            referrerPolicy="no-referrer"
          />
        )}
        {photo.media_type === "video" && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.55)" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
            </div>
          </div>
        )}
        <button
          onClick={toggleFeatured}
          className="absolute top-2 left-2 p-1.5 rounded-sm"
          style={{ background: photo.featured ? "#FFB500" : "rgba(255,255,255,0.85)", color: "#1C1917" }}
          title={photo.featured ? "Featured (industry hero)" : "Mark as industry hero"}
          data-testid={`photo-featured-${photo.id}`}
          aria-label="Toggle featured"
        >
          {photo.featured ? <Pin size={14} fill="currentColor" /> : <Star size={14} />}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="absolute top-2 right-2 p-1.5 rounded-sm hover:bg-red-100"
          style={{ background: "rgba(255,255,255,0.9)", color: "#B91C1C" }}
          title="Delete this item"
          aria-label="Delete"
          data-testid={`photo-delete-top-${photo.id}`}
        >
          <Trash2 size={14} />
        </button>
        {photo.drive_file_id && (
          <a
            href={`https://drive.google.com/file/d/${photo.drive_file_id}/view`}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute top-2 right-12 p-1.5 rounded-sm hover:bg-white/20"
            style={{ background: "rgba(255,255,255,0.85)", color: "#1C1917" }}
            title="Open in Google Drive"
            aria-label="Open in Drive"
          >
            <ExternalLink size={14} />
          </a>
        )}
        {(photo.industries || []).length === 0 && (
          <div
            className="absolute bottom-2 left-2 right-2 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 text-center"
            style={{ background: "#FEE2E2", color: "#991B1B" }}
          >
            ⚠ Untagged — won't appear in the gallery
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-center gap-2">
          <div className="text-[12px] font-bold truncate flex-1" title={photo.filename}>{photo.filename || "Untitled"}</div>
          {projectName && (
            <span
              className="text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm flex items-center gap-1 max-w-[55%] flex-shrink-0"
              style={{ background: "var(--ups-ink)", color: "var(--ups-gold)" }}
              data-testid={`photo-project-pill-${photo.id}`}
              title={`In project: ${projectName}`}
            >
              <Folder size={9} />
              <span className="truncate">{projectName}</span>
            </span>
          )}
        </div>
        <div className="text-[10px] font-mono text-[color:var(--ups-subtext)] mt-0.5">
          {(photo.industries || []).length} industry · {(photo.product_tags || []).length} tag{(photo.product_tags || []).length === 1 ? "" : "s"}
        </div>

        {!isOpen ? (
          <button
            onClick={() => setOpen(true)}
            className="mt-2 w-full px-2 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]"
            style={{ border: "1px solid var(--ups-border)" }}
            data-testid={`photo-edit-${photo.id}`}
          >
            Edit tags
          </button>
        ) : (
          <div className="mt-2 space-y-2">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Caption</span>
              <input
                type="text"
                value={draft.caption}
                onChange={(e) => setDraft({ ...draft, caption: e.target.value })}
                className="input-swiss w-full px-2 py-1 mt-0.5 rounded-sm text-[12px]"
                placeholder="e.g. Sushi Yama — 200 laminated menus"
              />
            </label>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-1">Industries</div>
              <div className="flex flex-wrap gap-1">
                {industries.map((i) => {
                  const on = draft.industries.includes(i.slug);
                  return (
                    <button
                      key={i.slug}
                      onClick={() => toggleIndustry(i.slug)}
                      className={`text-[10px] font-bold uppercase tracking-[0.12em] px-1.5 py-1 rounded-sm border ${on ? "bg-[color:var(--ups-ink)] text-[color:var(--ups-paper)]" : "bg-white hover:bg-[color:var(--ups-muted)]"}`}
                      style={{ borderColor: on ? "var(--ups-ink)" : "var(--ups-border)" }}
                      data-testid={`photo-${photo.id}-industry-${i.slug}`}
                    >
                      {i.emoji} {i.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-1">Product tags</div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {Object.entries(
                  productTags
                    .filter((t) => !t.archived)
                    .reduce((acc, t) => {
                      const c = t.category || "Other";
                      (acc[c] = acc[c] || []).push(t.name);
                      return acc;
                    }, {})
                ).map(([cat, names]) => (
                  <div key={cat}>
                    <div className="text-[8px] font-bold uppercase tracking-[0.2em] text-[color:var(--ups-subtext)] mb-0.5">{cat}</div>
                    <div className="flex flex-wrap gap-1">
                      {names.map((t) => {
                        const on = draft.product_tags.includes(t);
                        return (
                          <button
                            key={t}
                            onClick={() => toggleTag(t)}
                            className={`text-[10px] font-bold uppercase tracking-[0.12em] px-1.5 py-1 rounded-sm border ${on ? "bg-[#FFB500] text-[color:var(--ups-ink)]" : "bg-white hover:bg-[color:var(--ups-muted)]"}`}
                            style={{ borderColor: on ? "#FFB500" : "var(--ups-border)" }}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-between gap-2 pt-1">
              <button
                onClick={onDelete}
                className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1 text-red-700 hover:bg-red-50"
                style={{ border: "1px solid var(--ups-border)" }}
                data-testid={`photo-delete-${photo.id}`}
              >
                <Trash2 size={11} /> Delete
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="btn-brutal px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1 disabled:opacity-50"
                data-testid={`photo-save-${photo.id}`}
              >
                {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ============================================================== */
/* Settings modal — Drive folder URL + reminder for the API key   */
/* ============================================================== */
const PortfolioSettingsModal = ({ settings, onClose, onSaved }) => {
  const [folderUrl, setFolderUrl] = useState(settings?.drive_folder_url || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const fresh = await adminUpdateSettings({ drive_folder_url: folderUrl });
      toast.success("Saved");
      onSaved(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="portfolio-settings-modal">
      <button className="absolute inset-0 bg-black/50" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-lg rounded-sm overflow-hidden" style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
          <h3 className="font-display text-[18px] font-bold flex items-center gap-2">
            <Settings size={18} /> Portfolio Settings
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-white/10" aria-label="Close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Google Drive folder URL</label>
            <input
              type="url"
              value={folderUrl}
              onChange={(e) => setFolderUrl(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/XXXXXXXXXX"
              className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-[13px]"
              data-testid="portfolio-folder-url"
            />
            <p className="text-[11px] text-[color:var(--ups-subtext)] mt-1.5 leading-snug">
              The folder must be shared as <strong>"Anyone with the link can view"</strong>. We use Drive's public REST API with a server-side key — no per-user OAuth.
            </p>
          </div>
          <div
            className="p-3 rounded-sm text-[12px]"
            style={{
              background: settings?.drive_api_key_configured ? "#ECFDF5" : "#FEF3C7",
              border: `1px solid ${settings?.drive_api_key_configured ? "#10B981" : "#F59E0B"}`,
            }}
          >
            <div className="font-bold uppercase text-[10px] tracking-[0.14em] mb-0.5">
              {settings?.drive_api_key_configured ? "✓ API key configured" : "⚠ API key missing"}
            </div>
            <div className="leading-snug">
              {settings?.drive_api_key_configured
                ? "Drive API key is set on the server. You're ready to sync."
                : <>Add <code>GOOGLE_DRIVE_API_KEY</code> to <code>/app/backend/.env</code> then restart the backend. Get one from <a className="underline" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">console.cloud.google.com</a> after enabling the Drive API on your project.</>}
            </div>
          </div>
        </div>
        <div className="px-5 py-3 flex justify-end gap-2" style={{ borderTop: "1px solid var(--ups-border)" }}>
          <button onClick={onClose} className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]" style={{ border: "1px solid var(--ups-border)" }}>Cancel</button>
          <button onClick={save} disabled={saving} className="btn-brutal px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 disabled:opacity-50" data-testid="portfolio-settings-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

/* ============================================================== */
/* Manual Add modal — paste an image URL when Drive isn't set up  */
/* ============================================================== */
const ManualAddModal = ({ industries, productTags, onClose, onAdded }) => {
  const [draft, setDraft] = useState({
    image_url: "", filename: "", caption: "",
    industries: [], product_tags: [], featured: false,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!draft.image_url.trim()) { toast.error("Image URL is required"); return; }
    setSaving(true);
    try {
      await adminCreatePhoto(draft);
      toast.success("Added");
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="portfolio-manual-add-modal">
      <button className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-sm overflow-hidden" style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
          <h3 className="font-display text-[18px] font-bold flex items-center gap-2">
            <ImagePlus size={18} /> Add photo by URL
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-white/10"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Image URL *</span>
            <input type="url" value={draft.image_url} onChange={(e) => setDraft({ ...draft, image_url: e.target.value })} className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-[13px]" placeholder="https://…" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Caption</span>
            <input type="text" value={draft.caption} onChange={(e) => setDraft({ ...draft, caption: e.target.value })} className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-[13px]" />
          </label>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-1">Industries</div>
            <div className="flex flex-wrap gap-1">
              {industries.map((i) => {
                const on = draft.industries.includes(i.slug);
                return (
                  <button key={i.slug} onClick={() => setDraft({ ...draft, industries: on ? draft.industries.filter((s) => s !== i.slug) : [...draft.industries, i.slug] })} className={`text-[10px] font-bold uppercase tracking-[0.12em] px-1.5 py-1 rounded-sm border ${on ? "bg-[color:var(--ups-ink)] text-[color:var(--ups-paper)]" : "bg-white"}`} style={{ borderColor: on ? "var(--ups-ink)" : "var(--ups-border)" }}>
                    {i.emoji} {i.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-1">Product tags</div>
            <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
              {productTags.filter((t) => !t.archived).map((tagObj) => {
                const t = tagObj.name;
                const on = draft.product_tags.includes(t);
                return (
                  <button key={t} onClick={() => setDraft({ ...draft, product_tags: on ? draft.product_tags.filter((x) => x !== t) : [...draft.product_tags, t] })} className={`text-[10px] font-bold uppercase tracking-[0.12em] px-1.5 py-1 rounded-sm border ${on ? "bg-[#FFB500] text-[color:var(--ups-ink)]" : "bg-white"}`} style={{ borderColor: on ? "#FFB500" : "var(--ups-border)" }}>
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="px-5 py-3 flex justify-end gap-2" style={{ borderTop: "1px solid var(--ups-border)" }}>
          <button onClick={onClose} className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]" style={{ border: "1px solid var(--ups-border)" }}>Cancel</button>
          <button onClick={save} disabled={saving} className="btn-brutal px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 disabled:opacity-50" data-testid="portfolio-manual-add-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

export default PortfolioTab;

/* ================================================================== */
/* Product Tag Manager Modal — full CRUD with category grouping       */
/* ================================================================== */
const ProductTagManagerModal = ({ tags, categories, onClose, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newTag, setNewTag] = useState({ name: "", category: categories[0] || "Other" });
  const [editing, setEditing] = useState(null); // tag name or null
  const [editDraft, setEditDraft] = useState({ name: "", category: "Other" });

  const grouped = tags.reduce((acc, t) => {
    const c = t.category || "Other";
    (acc[c] = acc[c] || []).push(t);
    return acc;
  }, {});
  // Sort tags within each category alphabetically; categories follow the
  // canonical order from the server, with anything else appended.
  const orderedCats = [...categories, ...Object.keys(grouped).filter((c) => !categories.includes(c))]
    .filter((c) => grouped[c]);

  const handleCreate = async () => {
    const name = newTag.name.trim();
    if (!name) { toast.error("Tag name is required"); return; }
    setBusy(true);
    try {
      await adminCreateProductTag({ name, category: newTag.category });
      toast.success(`Added "${name}"`);
      setNewTag({ name: "", category: newTag.category });
      setAddOpen(false);
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  const beginEdit = (t) => {
    setEditing(t.name);
    setEditDraft({ name: t.name, category: t.category || "Other" });
  };

  const saveEdit = async (originalName) => {
    setBusy(true);
    try {
      await adminUpdateProductTag(originalName, { name: editDraft.name.trim(), category: editDraft.category });
      toast.success("Saved");
      setEditing(null);
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleArchive = async (t) => {
    try {
      await adminUpdateProductTag(t.name, { archived: !t.archived });
      await onChange();
    } catch (e) {
      toast.error("Could not toggle archive");
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(
      `Delete tag "${t.name}"?\n\nThis removes it from every photo currently using it. Photos themselves stay. Prefer "Archive" if you might re-use it later.`
    )) return;
    try {
      await adminDeleteProductTag(t.name);
      toast.success("Deleted");
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="product-tag-manager">
      <button className="absolute inset-0 bg-black/50" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-2xl rounded-sm overflow-hidden" style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
          <h3 className="font-display text-[18px] font-bold flex items-center gap-2">
            <Tag size={18} /> Product Tag Manager
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-white/10" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 max-h-[70vh] overflow-y-auto space-y-3">
          {/* Industries reminder */}
          <div className="text-[11px] text-[color:var(--ups-subtext)] leading-snug p-2.5 rounded-sm" style={{ background: "#F5F5F4", border: "1px solid var(--ups-border)" }}>
            <strong>Tip:</strong> Industries live in their own manager — open the <em>Industries</em> button at the top of the Portfolio tab to add or rename them.
          </div>

          {/* Add new tag — collapsed by default */}
          {!addOpen ? (
            <button
              onClick={() => setAddOpen(true)}
              className="w-full p-3 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center justify-center gap-2 hover:bg-[color:var(--ups-muted)]"
              style={{ border: "2px dashed var(--ups-border)" }}
              data-testid="tag-mgr-add-open"
            >
              <Plus size={14} /> Add new product tag
            </button>
          ) : (
            <div className="p-3 rounded-sm" style={{ background: "#FFF8E1", border: "2px solid #FFB500" }}>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,auto] gap-2 items-end">
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Tag name</span>
                  <input
                    type="text"
                    value={newTag.name}
                    onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                    placeholder="e.g. Water Bottles"
                    className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]"
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                    data-testid="tag-mgr-add-name"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Category</span>
                  <select
                    value={newTag.category}
                    onChange={(e) => setNewTag({ ...newTag, category: e.target.value })}
                    className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]"
                    data-testid="tag-mgr-add-category"
                  >
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setAddOpen(false)}
                    className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-white"
                    style={{ border: "1px solid var(--ups-border)" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreate}
                    disabled={busy}
                    className="btn-brutal px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1 disabled:opacity-50"
                    data-testid="tag-mgr-add-save"
                  >
                    {busy ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Grouped tag list */}
          {orderedCats.length === 0 ? (
            <div className="py-8 text-center text-sm text-[color:var(--ups-subtext)]">No product tags yet.</div>
          ) : orderedCats.map((cat) => (
            <div key={cat}>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[color:var(--ups-subtext)] mb-1.5 mt-3">
                {cat} <span className="opacity-60">· {grouped[cat].length}</span>
              </div>
              <div className="space-y-1">
                {grouped[cat].map((t) => (
                  <div
                    key={t.name}
                    className="flex items-center gap-2 p-2 rounded-sm"
                    style={{
                      background: t.archived ? "#F5F5F4" : "#fff",
                      border: "1px solid var(--ups-border)",
                      opacity: t.archived ? 0.6 : 1,
                    }}
                    data-testid={`tag-row-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {editing === t.name ? (
                      <>
                        <input
                          type="text"
                          value={editDraft.name}
                          onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                          className="input-swiss flex-1 px-2 py-1 rounded-sm text-[12px]"
                          autoFocus
                          onKeyDown={(e) => e.key === "Enter" && saveEdit(t.name)}
                        />
                        <select
                          value={editDraft.category}
                          onChange={(e) => setEditDraft({ ...editDraft, category: e.target.value })}
                          className="input-swiss px-2 py-1 rounded-sm text-[12px]"
                        >
                          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button
                          onClick={() => saveEdit(t.name)}
                          disabled={busy}
                          className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm bg-[color:var(--ups-ink)] text-[color:var(--ups-paper)]"
                          data-testid={`tag-row-save-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                          <Save size={11} />
                        </button>
                        <button onClick={() => setEditing(null)} className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm" style={{ border: "1px solid var(--ups-border)" }}>
                          <X size={11} />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-[13px] font-bold flex-1">{t.name}</span>
                        {t.archived && <span className="text-[9px] uppercase tracking-[0.2em] font-bold opacity-60">Archived</span>}
                        <button onClick={() => beginEdit(t)} className="p-1 rounded-sm hover:bg-[color:var(--ups-muted)]" title="Edit" data-testid={`tag-row-edit-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          <Pencil size={12} />
                        </button>
                        <button onClick={() => toggleArchive(t)} className="p-1 rounded-sm hover:bg-[color:var(--ups-muted)]" title={t.archived ? "Unarchive" : "Archive"}>
                          <Archive size={12} />
                        </button>
                        <button onClick={() => handleDelete(t)} className="p-1 rounded-sm hover:bg-red-50 text-red-700" title="Delete" data-testid={`tag-row-delete-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="px-4 py-3 flex justify-end" style={{ borderTop: "1px solid var(--ups-border)" }}>
          <button onClick={onClose} className="px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]" style={{ border: "1px solid var(--ups-border)" }} data-testid="tag-mgr-close">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Upload Photos Modal — drag-drop multi-file upload to R2            */
/* ================================================================== */
const UploadPhotosModal = ({ onClose, onUploaded }) => {
  const [files, setFiles] = useState([]); // [{file, preview, kind}]
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [groupAsProject, setGroupAsProject] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");

  const addFiles = (incoming) => {
    const filtered = Array.from(incoming).filter(
      (f) => f.type.startsWith("image/") || f.type.startsWith("video/"),
    );
    const withPreviews = filtered.map((f) => ({
      file: f,
      preview: URL.createObjectURL(f),
      kind: f.type.startsWith("video/") ? "video" : "image",
    }));
    setFiles((prev) => [...prev, ...withPreviews]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const removeFile = (idx) => {
    setFiles((prev) => {
      URL.revokeObjectURL(prev[idx]?.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const submit = async () => {
    if (files.length === 0) { toast.error("Pick at least one photo"); return; }
    if (groupAsProject && !projectName.trim()) {
      toast.error("Project name is required");
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      const opts = groupAsProject ? {
        projectName: projectName.trim(),
        projectDescription: projectDescription.trim(),
      } : {};
      const res = await adminUploadPhotos(
        files.map((x) => x.file),
        (p) => setProgress(p),
        opts,
      );
      const grouped = groupAsProject ? ` into project "${projectName.trim()}"` : "";
      toast.success(`Uploaded ${res.created} item${res.created === 1 ? "" : "s"}${grouped}`);
      files.forEach((f) => URL.revokeObjectURL(f.preview));
      setFiles([]);
      setProjectName("");
      setProjectDescription("");
      setGroupAsProject(false);
      await onUploaded();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const totalMB = (files.reduce((sum, f) => sum + (f.file.size || 0), 0) / (1024 * 1024)).toFixed(1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="portfolio-upload-modal">
      <button className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} aria-label="Close" />
      <div className="relative w-full max-w-2xl rounded-sm overflow-hidden" style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
          <h3 className="font-display text-[18px] font-bold flex items-center gap-2">
            <Upload size={18} /> Upload Photos
          </h3>
          <button onClick={onClose} disabled={busy} className="p-1.5 rounded-sm hover:bg-white/10 disabled:opacity-50" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {/* Drop zone */}
          <label
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className="block p-8 rounded-sm cursor-pointer text-center transition"
            style={{
              border: `2px dashed ${dragOver ? "#FFB500" : "var(--ups-border)"}`,
              background: dragOver ? "#FFF8E1" : "var(--ups-muted)",
            }}
            data-testid="portfolio-upload-dropzone"
          >
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => addFiles(e.target.files || [])}
              data-testid="portfolio-upload-input"
            />
            <Upload size={28} className="mx-auto mb-2 opacity-60" />
            <div className="text-[13px] font-bold uppercase tracking-[0.14em]">
              Drop photos or videos here or click to pick
            </div>
            <div className="text-[11px] text-[color:var(--ups-subtext)] mt-1">
              Images: JPG · PNG · WebP · HEIC · Videos: MP4 · MOV · WebM — up to 100 MB per file
            </div>
          </label>

          {/* Selected preview grid */}
          {files.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">
                  {files.length} selected · {totalMB} MB
                </span>
                <button
                  onClick={() => { files.forEach((f) => URL.revokeObjectURL(f.preview)); setFiles([]); }}
                  disabled={busy}
                  className="text-[10px] font-bold uppercase tracking-[0.14em] hover:underline disabled:opacity-50"
                >
                  Clear all
                </button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {files.map((f, i) => (
                  <div key={i} className="relative group" data-testid={`upload-preview-${i}`}>
                    {f.kind === "video" ? (
                      <div className="relative w-full aspect-square rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-border)" }}>
                        <video src={f.preview} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.6)" }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <img src={f.preview} alt={f.file.name} className="w-full aspect-square object-cover rounded-sm" style={{ border: "1px solid var(--ups-border)" }} />
                    )}
                    {!busy && (
                      <button
                        onClick={() => removeFile(i)}
                        className="absolute -top-1 -right-1 p-1 rounded-full bg-red-600 text-white opacity-0 group-hover:opacity-100 transition"
                        aria-label="Remove"
                      >
                        <X size={10} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Group as project toggle */}
          {files.length > 0 && (
            <div className="rounded-sm p-3" style={{ background: files.length >= 2 ? "#FFF8E1" : "var(--ups-muted)", border: "1px solid var(--ups-border)" }}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={groupAsProject}
                  onChange={(e) => setGroupAsProject(e.target.checked)}
                  disabled={busy}
                  data-testid="upload-group-toggle"
                />
                <span className="text-[12px] font-bold uppercase tracking-[0.14em]">
                  Group these {files.length} items into a single project
                </span>
              </label>
              {groupAsProject && (
                <div className="mt-3 space-y-2">
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Project name *</span>
                    <input
                      type="text"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      placeholder="e.g. Reformation Bistro — Storefront Rebrand"
                      className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]"
                      data-testid="upload-project-name"
                      disabled={busy}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Description</span>
                    <textarea
                      value={projectDescription}
                      onChange={(e) => setProjectDescription(e.target.value)}
                      placeholder="1–2 sentences about this job — appears on the project's public page."
                      rows={2}
                      className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[12px] resize-none"
                      data-testid="upload-project-description"
                      disabled={busy}
                    />
                  </label>
                  <p className="text-[10px] text-[color:var(--ups-subtext)]">
                    The first item will be the project cover. You can change the cover, edit details, or assign industry & product tags from the project's row after upload.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Progress bar */}
          {busy && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] mb-1">Uploading… {progress}%</div>
              <div className="w-full h-2 rounded-sm overflow-hidden" style={{ background: "var(--ups-muted)" }}>
                <div className="h-full transition-all" style={{ width: `${progress}%`, background: "#FFB500" }} />
              </div>
            </div>
          )}

          <p className="text-[11px] text-[color:var(--ups-subtext)] leading-snug">
            <strong>Tip:</strong> photos and videos upload to your Cloudflare R2 bucket — no Google login needed. Once uploaded, click each card to tag it with industry + product.
          </p>
        </div>

        <div className="px-5 py-3 flex justify-end gap-2" style={{ borderTop: "1px solid var(--ups-border)" }}>
          <button onClick={onClose} disabled={busy} className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)] disabled:opacity-50" style={{ border: "1px solid var(--ups-border)" }}>
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || files.length === 0}
            className="btn-brutal px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 disabled:opacity-50"
            data-testid="portfolio-upload-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {busy ? "Uploading…" : `Upload ${files.length || ""}`}
          </button>
        </div>
      </div>
    </div>
  );
};


/* ================================================================== */
/* Industry Manager Modal — slug + label CRUD                         */
/* ================================================================== */
const slugify = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const IndustryManagerModal = ({ industries, onClose, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newRow, setNewRow] = useState({ slug: "", label: "" });
  const [slugTouched, setSlugTouched] = useState(false);
  const [editing, setEditing] = useState(null); // slug or null
  const [editDraft, setEditDraft] = useState({ label: "" });

  const handleLabelChange = (label) => {
    setNewRow((prev) => ({
      label,
      slug: slugTouched ? prev.slug : slugify(label),
    }));
  };

  const handleCreate = async () => {
    const label = newRow.label.trim();
    const slug = (newRow.slug.trim() || slugify(label));
    if (!label) { toast.error("Label is required"); return; }
    if (!slug) { toast.error("Slug is required"); return; }
    setBusy(true);
    try {
      await adminCreateIndustry({ slug, label });
      toast.success(`Added "${label}"`);
      setNewRow({ slug: "", label: "" });
      setSlugTouched(false);
      setAddOpen(false);
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  const beginEdit = (i) => {
    setEditing(i.slug);
    setEditDraft({ label: i.label });
  };

  const saveEdit = async (slug) => {
    const label = editDraft.label.trim();
    if (!label) { toast.error("Label cannot be empty"); return; }
    setBusy(true);
    try {
      await adminUpdateIndustry(slug, { label });
      toast.success("Saved");
      setEditing(null);
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleArchive = async (i) => {
    try {
      await adminUpdateIndustry(i.slug, { archived: !i.archived });
      await onChange();
    } catch (e) {
      toast.error("Could not toggle archive");
    }
  };

  const handleDelete = async (i) => {
    if (!window.confirm(
      `Delete industry "${i.label}"?\n\nThis removes it from every photo currently tagged with it. Photos themselves stay. Prefer "Archive" if you might re-use it later.`
    )) return;
    try {
      await adminDeleteIndustry(i.slug);
      toast.success("Deleted");
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="industry-manager">
      <button className="absolute inset-0 bg-black/50" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-2xl rounded-sm overflow-hidden" style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
          <h3 className="font-display text-[18px] font-bold flex items-center gap-2">
            <Building2 size={18} /> Industry Manager
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-white/10" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 max-h-[70vh] overflow-y-auto space-y-3">
          <div className="text-[11px] text-[color:var(--ups-subtext)] leading-snug p-2.5 rounded-sm" style={{ background: "#F5F5F4", border: "1px solid var(--ups-border)" }}>
            <strong>Tip:</strong> The slug is used in the public URL like <code>/portfolio/restaurants</code>. Editing the label updates the displayed name; the slug stays the same once created so existing shared links keep working.
          </div>

          {!addOpen ? (
            <button
              onClick={() => setAddOpen(true)}
              className="w-full p-3 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center justify-center gap-2 hover:bg-[color:var(--ups-muted)]"
              style={{ border: "2px dashed var(--ups-border)" }}
              data-testid="industry-mgr-add-open"
            >
              <Plus size={14} /> Add new industry
            </button>
          ) : (
            <div className="p-3 rounded-sm" style={{ background: "#FFF8E1", border: "2px solid #FFB500" }}>
              <div className="grid grid-cols-1 sm:grid-cols-[1.2fr,1fr,auto] gap-2 items-end">
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Label</span>
                  <input
                    type="text"
                    value={newRow.label}
                    onChange={(e) => handleLabelChange(e.target.value)}
                    placeholder="e.g. Restaurants & Bars"
                    className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]"
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                    data-testid="industry-mgr-add-label"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Slug (URL)</span>
                  <input
                    type="text"
                    value={newRow.slug}
                    onChange={(e) => { setSlugTouched(true); setNewRow({ ...newRow, slug: e.target.value }); }}
                    placeholder="restaurants"
                    className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px] font-mono"
                    onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                    data-testid="industry-mgr-add-slug"
                  />
                </label>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setAddOpen(false); setNewRow({ slug: "", label: "" }); setSlugTouched(false); }}
                    className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-white"
                    style={{ border: "1px solid var(--ups-border)" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreate}
                    disabled={busy}
                    className="btn-brutal px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1 disabled:opacity-50"
                    data-testid="industry-mgr-add-save"
                  >
                    {busy ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Industry list */}
          {industries.length === 0 ? (
            <div className="py-8 text-center text-sm text-[color:var(--ups-subtext)]">No industries yet.</div>
          ) : (
            <div className="space-y-1">
              {industries.map((i) => (
                <div
                  key={i.slug}
                  className="flex items-center gap-2 p-2 rounded-sm"
                  style={{
                    background: i.archived ? "#F5F5F4" : "#fff",
                    border: "1px solid var(--ups-border)",
                    opacity: i.archived ? 0.6 : 1,
                  }}
                  data-testid={`industry-row-${i.slug}`}
                >
                  {editing === i.slug ? (
                    <>
                      <input
                        type="text"
                        value={editDraft.label}
                        onChange={(e) => setEditDraft({ label: e.target.value })}
                        className="input-swiss flex-1 px-2 py-1 rounded-sm text-[12px]"
                        autoFocus
                        onKeyDown={(e) => e.key === "Enter" && saveEdit(i.slug)}
                      />
                      <span className="text-[10px] font-mono text-[color:var(--ups-subtext)] px-1">{i.slug}</span>
                      <button
                        onClick={() => saveEdit(i.slug)}
                        disabled={busy}
                        className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm bg-[color:var(--ups-ink)] text-[color:var(--ups-paper)]"
                        data-testid={`industry-row-save-${i.slug}`}
                      >
                        <Save size={11} />
                      </button>
                      <button onClick={() => setEditing(null)} className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm" style={{ border: "1px solid var(--ups-border)" }}>
                        <X size={11} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-[13px] font-bold flex-1">{i.label}</span>
                      <span className="text-[10px] font-mono text-[color:var(--ups-subtext)]">/{i.slug}</span>
                      {i.archived && <span className="text-[9px] uppercase tracking-[0.2em] font-bold opacity-60">Archived</span>}
                      <button onClick={() => beginEdit(i)} className="p-1 rounded-sm hover:bg-[color:var(--ups-muted)]" title="Edit" data-testid={`industry-row-edit-${i.slug}`}>
                        <Pencil size={12} />
                      </button>
                      <button onClick={() => toggleArchive(i)} className="p-1 rounded-sm hover:bg-[color:var(--ups-muted)]" title={i.archived ? "Unarchive" : "Archive"}>
                        <Archive size={12} />
                      </button>
                      <button onClick={() => handleDelete(i)} className="p-1 rounded-sm hover:bg-red-50 text-red-700" title="Delete" data-testid={`industry-row-delete-${i.slug}`}>
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-4 py-3 flex justify-end" style={{ borderTop: "1px solid var(--ups-border)" }}>
          <button onClick={onClose} className="px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]" style={{ border: "1px solid var(--ups-border)" }} data-testid="industry-mgr-close">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};



/* ================================================================== */
/* Project Card — admin list entry showing cover + name + count        */
/* ================================================================== */
const ProjectCard = ({ project, onEdit, onChanged }) => {
  const handleDelete = async () => {
    const choice = window.prompt(
      `Delete project "${project.name}"?\n\nType:\n  ungroup — to keep the photos but unlink them\n  delete  — to remove project AND every photo from storage\n  cancel  — to do nothing`,
      "ungroup",
    );
    if (!choice || choice === "cancel") return;
    const deleteMedia = choice.toLowerCase().startsWith("delete");
    if (deleteMedia && !window.confirm(`This will permanently delete ${project.media_count} item${project.media_count === 1 ? "" : "s"} from R2 storage. Continue?`)) return;
    try {
      await adminDeleteProject(project.id, { deleteMedia });
      toast.success(deleteMedia ? "Project + media deleted" : "Project ungrouped");
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const coverUrl = project.cover_url;
  const coverIsVideo = project.cover_media_type === "video";

  return (
    <div className="rounded-sm overflow-hidden" style={{ background: "#fff", border: "1px solid var(--ups-border)" }} data-testid={`admin-project-card-${project.id}`}>
      <div className="relative aspect-video bg-stone-200 overflow-hidden">
        {coverUrl ? (
          coverIsVideo ? (
            <video src={coverUrl} muted playsInline preload="metadata" className="w-full h-full object-cover" />
          ) : (
            <img src={coverUrl} alt={project.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[color:var(--ups-subtext)]"><Folder size={28} /></div>
        )}
        <div className="absolute top-2 left-2 px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-[0.14em] flex items-center gap-1" style={{ background: "var(--ups-ink)", color: "var(--ups-gold)" }}>
          <Folder size={11} /> {project.media_count} items
        </div>
      </div>
      <div className="p-3 space-y-2">
        <div className="font-bold text-[14px] truncate" title={project.name}>{project.name}</div>
        {project.description && (
          <div className="text-[11px] text-[color:var(--ups-subtext)] line-clamp-2">{project.description}</div>
        )}
        <div className="flex flex-wrap gap-1">
          {(project.industries || []).map((s) => (
            <span key={s} className="text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm" style={{ background: "var(--ups-gold)", color: "var(--ups-ink)" }}>
              {s}
            </span>
          ))}
          {(project.product_tags || []).slice(0, 4).map((t) => (
            <span key={t} className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm" style={{ background: "var(--ups-muted)" }}>{t}</span>
          ))}
        </div>
        <div className="flex items-center gap-1 pt-1">
          <button onClick={onEdit} className="flex-1 px-2 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)] flex items-center justify-center gap-1" style={{ border: "1px solid var(--ups-border)" }} data-testid={`project-edit-${project.id}`}>
            <Pencil size={11} /> Edit
          </button>
          <a href={`/portfolio/project/${project.id}`} target="_blank" rel="noopener noreferrer" className="px-2 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)] flex items-center gap-1" style={{ border: "1px solid var(--ups-border)" }} title="Open public page">
            <ExternalLink size={11} />
          </a>
          <button onClick={handleDelete} className="px-2 py-1.5 text-[11px] font-bold rounded-sm hover:bg-red-50 text-red-700" style={{ border: "1px solid #FCA5A5" }} title="Delete project" data-testid={`project-delete-${project.id}`}>
            <Trash2 size={11} />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Project Edit Modal — name, description, tags, cover, member mgmt     */
/* ================================================================== */
const ProjectEditModal = ({ project, industries, productTags, onClose, onSaved }) => {
  const [name, setName] = useState(project.name || "");
  const [description, setDescription] = useState(project.description || "");
  const [selectedIndustries, setSelectedIndustries] = useState(project.industries || []);
  const [selectedTags, setSelectedTags] = useState(project.product_tags || []);
  const [coverId, setCoverId] = useState(project.cover_photo_id || (project.media?.[0]?.id ?? null));
  const [busy, setBusy] = useState(false);
  const [media, setMedia] = useState(project.media || []);

  const toggleIndustry = (slug) => setSelectedIndustries((cur) => cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug]);
  const toggleTag = (t) => setSelectedTags((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]);

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await adminUpdateProject(project.id, {
        name: name.trim(),
        description: description.trim(),
        industries: selectedIndustries,
        product_tags: selectedTags,
        cover_photo_id: coverId,
      });
      toast.success("Saved");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const setCover = async (photoId) => {
    setCoverId(photoId);
    try {
      await adminSetProjectCover(project.id, photoId);
      toast.success("Cover updated");
    } catch (e) {
      toast.error("Cover update failed");
    }
  };

  const removeMember = async (photoId) => {
    if (!window.confirm("Remove this item from the project? The item itself stays in your portfolio as a standalone item.")) return;
    try {
      const updated = await adminRemovePhotoFromProject(project.id, photoId);
      setMedia(updated.media || []);
      if (updated.cover_photo_id) setCoverId(updated.cover_photo_id);
      toast.success("Removed");
    } catch (e) {
      toast.error("Remove failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="project-edit-modal">
      <button className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} aria-label="Close" />
      <div className="relative w-full max-w-3xl rounded-sm overflow-hidden" style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
          <h3 className="font-display text-[18px] font-bold flex items-center gap-2"><Folder size={18} /> Edit project</h3>
          <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-white/10" aria-label="Close"><X size={16} /></button>
        </div>

        <div className="p-4 max-h-[70vh] overflow-y-auto space-y-4">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Name *</span>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[14px] font-bold"
              data-testid="project-edit-name"
            />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Description</span>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
              className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[12px] resize-none"
              data-testid="project-edit-description"
            />
          </label>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-1">Industries</div>
            <div className="flex flex-wrap gap-1.5">
              {industries.map((i) => (
                <button key={i.slug} type="button" onClick={() => toggleIndustry(i.slug)}
                  className={`px-2 py-1 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm ${selectedIndustries.includes(i.slug) ? "" : "hover:bg-[color:var(--ups-muted)]"}`}
                  style={selectedIndustries.includes(i.slug)
                    ? { background: "var(--ups-gold)", border: "1px solid var(--ups-ink)" }
                    : { background: "#fff", border: "1px solid var(--ups-border)" }}
                  data-testid={`project-industry-${i.slug}`}
                >
                  {i.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-1">Product tags</div>
            <div className="flex flex-wrap gap-1.5">
              {productTags.filter((t) => !t.archived).map((t) => (
                <button key={t.name} type="button" onClick={() => toggleTag(t.name)}
                  className={`px-2 py-1 text-[11px] font-bold rounded-sm ${selectedTags.includes(t.name) ? "" : "hover:bg-[color:var(--ups-muted)]"}`}
                  style={selectedTags.includes(t.name)
                    ? { background: "var(--ups-ink)", color: "var(--ups-paper)", border: "1px solid var(--ups-ink)" }
                    : { background: "#fff", border: "1px solid var(--ups-border)" }}
                >
                  {t.name}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-[color:var(--ups-subtext)] mt-1">Industry + product tags are inherited by every item in the project so it shows up on those industry and product pages.</p>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-1">Items ({media.length}) — click a thumbnail to set the cover</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {media.map((m) => (
                <div key={m.id} className="relative group" data-testid={`project-member-${m.id}`}>
                  <button
                    onClick={() => setCover(m.id)}
                    className="block w-full aspect-square overflow-hidden rounded-sm"
                    style={{ border: coverId === m.id ? "3px solid #FFB500" : "1px solid var(--ups-border)" }}
                    title="Set as cover"
                  >
                    {m.media_type === "video" ? (
                      <video src={m.image_url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    ) : (
                      <img src={m.thumb_url || m.image_url} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                    )}
                  </button>
                  {coverId === m.id && (
                    <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-[0.14em]" style={{ background: "#FFB500", color: "var(--ups-ink)" }}>
                      Cover
                    </div>
                  )}
                  <button onClick={() => removeMember(m.id)} className="absolute -top-1 -right-1 p-1 rounded-full bg-red-600 text-white opacity-0 group-hover:opacity-100 transition" aria-label="Remove from project">
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 flex justify-end gap-2" style={{ borderTop: "1px solid var(--ups-border)" }}>
          <button onClick={onClose} className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]" style={{ border: "1px solid var(--ups-border)" }}>Cancel</button>
          <button onClick={save} disabled={busy} className="btn-brutal px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 disabled:opacity-50" data-testid="project-edit-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
};
